import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { ConsoleSurface, SectionHeading } from "@/shared/ui";
import {
  DashboardWidgetContentState,
  type DashboardWidgetContentStateKind,
} from "@/features/deployment-dashboard/widgets/dashboard-widget-content-state";
import {
  DashboardWidgetIssueIndicator,
  type DashboardWidgetIssue,
} from "@/features/deployment-dashboard/widgets/dashboard-widget-issue-indicator";

export type { DashboardWidgetIssue as MetricCardIssue } from "@/features/deployment-dashboard/widgets/dashboard-widget-issue-indicator";

export interface MetricOccupancySummary {
  label: string;
}

export interface MetricCardValue {
  label?: string;
  value: string;
  valueSuffix?: string;
}

export interface MetricCardProps {
  label: string;
  value?: string;
  valueSuffix?: string;
  valueLabel?: string;
  secondaryValue?: MetricCardValue;
  note?: string;
  occupancySummary?: MetricOccupancySummary;
  issue?: DashboardWidgetIssue;
  contentState?: DashboardWidgetContentStateKind;
  emphasizeValue?: boolean;
  icon?: ReactNode;
}

/** Compact dashboard KPI card with optional issue and loading/empty content states. */
export function MetricCard({
  label,
  value,
  valueSuffix,
  valueLabel,
  secondaryValue,
  note,
  occupancySummary,
  issue,
  contentState,
  emphasizeValue = false,
  icon,
}: MetricCardProps) {
  const metricValues: MetricCardValue[] = [];
  if (value != null) metricValues.push({ label: valueLabel, value, valueSuffix });
  if (secondaryValue != null) metricValues.push(secondaryValue);

  return (
    <ConsoleSurface
      sx={{
        p: 2.25,
        display: "flex",
        flexDirection: "column",
        minHeight: 150,
      }}
    >
      <SectionHeading
        icon={icon}
        action={issue == null ? undefined : <DashboardWidgetIssueIndicator issue={issue} />}
      >
        {label}
      </SectionHeading>
      {contentState != null ? (
        <DashboardWidgetContentState state={contentState} minHeight={104} />
      ) : (
        <>
          <Box
            sx={{
              display: secondaryValue == null ? "flex" : "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
              flex: 1,
              alignItems: "stretch",
              justifyContent: emphasizeValue ? "center" : "flex-start",
              minHeight: 0,
              ...(secondaryValue == null
                ? {}
                : {
                    "& > :not(:first-of-type)": {
                      borderLeft: 1,
                      borderColor: "divider",
                      pl: 2,
                    },
                  }),
            }}
          >
            {metricValues.map((metric) => (
              <Box
                key={metric.label ?? metric.value}
                sx={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {metric.label != null && (
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75 }}
                  >
                    {metric.label}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
                  <Typography
                    variant={emphasizeValue ? "h2" : secondaryValue == null ? "h3" : "h4"}
                    component="p"
                    sx={{ fontWeight: 700, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
                  >
                    {metric.value}
                  </Typography>
                  {metric.valueSuffix != null && (
                    <Typography variant="body2" color="text.secondary">
                      {metric.valueSuffix}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          {occupancySummary != null && (
            <Typography variant="caption" color="text.secondary">
              {occupancySummary.label}
            </Typography>
          )}
          {note != null && occupancySummary == null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
            >
              {note}
            </Typography>
          )}
        </>
      )}
    </ConsoleSurface>
  );
}
